import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardService } from '../services/dashboardService';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionContext';
import api from '../services/api';
import './Dashboard.css';

import { formatDateOnly } from '../utils/dateOnly';
// Who's Out Today Widget Component
const WhosOutWidget = ({ employees, loading, onViewAll, onViewCalendar }) => {
  if (loading) {
    return (
      <div className="whos-out-widget" style={{ boxShadow: '0 2px 16px rgba(0,0,0,0.25), 0 0 15px rgba(212,169,78,0.12), 0 0 30px rgba(212,169,78,0.06)' }}>
        <div className="widget-header">
          <h3>📅 Who's Out Today</h3>
        </div>
        <div className="widget-content loading">
          <div className="loading-spinner-small"></div>
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="whos-out-widget" style={{ boxShadow: '0 2px 16px rgba(0,0,0,0.25), 0 0 15px rgba(212,169,78,0.12), 0 0 30px rgba(212,169,78,0.06)' }}>
      <div className="widget-header">
        <h3>📅 Who's Out Today</h3>
        <span className="out-count">{employees.length}</span>
      </div>
      <div className="widget-content">
        {employees.length === 0 ? (
          <div className="no-absences">
            <span className="check-icon">✓</span>
            <p>Everyone is in today!</p>
          </div>
        ) : (
          <div className="absence-list">
            {employees.slice(0, 5).map((emp, index) => (
              <div key={index} className="absence-item">
                {/* TO1b ruling 9: the leave TYPE is stripped server-side for a
                    viewer who is not an approver and not the person concerned,
                    so this widget shows the department instead when it is
                    absent. Colour comes from the department (R-TO7 direction).
                    TO1c round 2 (ledger (ix)): the type's COLOUR is now
                    stripped alongside the type name, so for a view-only viewer
                    `emp.color` is absent too and the chain lands on
                    department_color — or on the grey default for someone whose
                    department text matches no active department. The
                    `emp.color` leg still serves approvers, who receive both. */}
                <div className="absence-avatar" style={{ backgroundColor: emp.department_color || emp.color || '#A0A0A0' }}>
                  {emp.employee_name?.charAt(0) || '?'}
                </div>
                <div className="absence-info">
                  <span className="absence-name">{emp.employee_name}</span>
                  <span className="absence-type" style={{ color: emp.department_color || emp.color || '#A0A0A0' }}>
                    {/* TO1c (ruling 10): the department colour dot, the same
                        colour dimension the Who's Out calendar uses. */}
                    <span className="absence-dot" style={{
                      display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%',
                      marginRight: '6px', verticalAlign: 'middle',
                      backgroundColor: emp.department_color || emp.color || '#A0A0A0'
                    }} />
                    {emp.leave_type || emp.department || 'Out'}
                    {emp.workers_comp_case_id && <span className="wc-badge">WC</span>}
                  </span>
                </div>
                {emp.expected_return_date && (
                  <div className="absence-return">
                    <small>Returns: {formatDateOnly(emp.expected_return_date)}</small>
                  </div>
                )}
              </div>
            ))}
            {employees.length > 5 && (
              <div className="more-absences">
                +{employees.length - 5} more
              </div>
            )}
          </div>
        )}
      </div>
      <div className="widget-footer">
        <button onClick={onViewAll} className="view-all-btn">
          View Attendance →
        </button>
        {/* TO1c (ruling 8): the full calendar the widget summarises. This
            widget only renders for time_off:view holders, which is exactly
            who /whos-out admits. */}
        <button onClick={onViewCalendar} className="view-all-btn">
          View calendar →
        </button>
      </div>
    </div>
  );
};

// Pending Time-Off Requests Widget
const PendingRequestsWidget = ({ requests, loading, onViewAll }) => {
  if (loading) {
    return (
      <div className="pending-requests-widget" style={{ boxShadow: '0 2px 16px rgba(0,0,0,0.25), 0 0 15px rgba(212,169,78,0.12), 0 0 30px rgba(212,169,78,0.06)' }}>
        <div className="widget-header">
          <h3>⏳ Pending Requests</h3>
        </div>
        <div className="widget-content loading">
          <div className="loading-spinner-small"></div>
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="pending-requests-widget" style={{ boxShadow: '0 2px 16px rgba(0,0,0,0.25), 0 0 15px rgba(212,169,78,0.12), 0 0 30px rgba(212,169,78,0.06)' }}>
      <div className="widget-header">
        <h3>⏳ Pending Requests</h3>
        {requests.length > 0 && (
          <span className="pending-count">{requests.length}</span>
        )}
      </div>
      <div className="widget-content">
        {requests.length === 0 ? (
          <div className="no-requests">
            <span className="check-icon">✓</span>
            <p>No pending requests</p>
          </div>
        ) : (
          <div className="request-list">
            {requests.slice(0, 3).map((req, index) => (
              <div key={index} className="request-item">
                <div className="request-avatar" style={{ backgroundColor: req.color || '#A0A0A0' }}>
                  {req.employee_name?.charAt(0) || '?'}
                </div>
                <div className="request-info">
                  <span className="request-name">{req.employee_name}</span>
                  <span className="request-dates">
                    {formatDateOnly(req.start_date)} - {formatDateOnly(req.end_date)}
                  </span>
                  <span className="request-type" style={{ color: req.color || '#A0A0A0' }}>
                    {req.type_name} ({req.hours_requested}h)
                  </span>
                </div>
              </div>
            ))}
            {requests.length > 3 && (
              <div className="more-requests">
                +{requests.length - 3} more pending
              </div>
            )}
          </div>
        )}
      </div>
      {requests.length > 0 && (
        <div className="widget-footer">
          <button onClick={onViewAll} className="view-all-btn">
            Review Requests →
          </button>
        </div>
      )}
    </div>
  );
};

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [heroImage, setHeroImage] = useState('https://images.unsplash.com/photo-1557223562-6c77ef16210f?w=1600&h=400&fit=crop&crop=center&q=80');
  const [outToday, setOutToday] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loadingAttendance, setLoadingAttendance] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();

  // TO1a (R-TO3/R-TO1): the hardcoded role list is retired. The two widgets
  // split on DIFFERENT actions because they show different things — Who's Out
  // is the view surface (ruling 9: everyone but drivers sees WHO is off),
  // Pending Requests is an approver surface.
  const canSeeWhosOut = hasPermission('time_off', 'view');
  const canSeePendingRequests = hasPermission('time_off', 'approve');
  const canSeeAttendance = canSeeWhosOut || canSeePendingRequests;

  useEffect(() => {
    fetchDashboardData();
    if (canSeeAttendance) {
      fetchAttendanceData();
    }
  }, [canSeeAttendance, canSeeWhosOut, canSeePendingRequests]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [statsData, settingsData] = await Promise.all([
        dashboardService.getStats(),
        api.get('/settings').then(res => res.data.grouped || {}).catch(() => ({}))
      ]);
      setStats(statsData);
      
      // Get hero image from appearance settings
      const appearanceSettings = settingsData.appearance || [];
      const dashboardSettings = settingsData.dashboard || [];
      
      // Check both categories for hero_image_url
      const heroSetting = appearanceSettings.find(s => s.setting_key === 'hero_image_url') 
        || dashboardSettings.find(s => s.setting_key === 'hero_image_url');
      
      if (heroSetting?.setting_value) {
        setHeroImage(heroSetting.setting_value);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendanceData = async () => {
    try {
      setLoadingAttendance(true);
      const [outTodayData, pendingData] = await Promise.all([
        canSeeWhosOut
          ? api.get('/time-off/out-today').then(res => res.data.employees || []).catch(() => [])
          : Promise.resolve([]),
        canSeePendingRequests
          ? api.get('/time-off/requests/pending').then(res => res.data.requests || []).catch(() => [])
          : Promise.resolve([])
      ]);
      setOutToday(outTodayData);
      setPendingRequests(pendingData);
    } catch (err) {
      console.error('Error fetching attendance data:', err);
    } finally {
      setLoadingAttendance(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div style={{ fontSize: '64px', animation: 'bounce 1s infinite' }}>🚌</div>
        <p>Loading dashboard...</p>
        <style>{`
          @keyframes bounce {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-20px); }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-error">
        <p>Error loading dashboard: {error}</p>
        <button onClick={fetchDashboardData}>Retry</button>
      </div>
    );
  }

  // Dynamic hero background style
  const heroStyle = {
    backgroundImage: `linear-gradient(135deg,
      rgba(30, 30, 30, 0.92) 0%,
      rgba(212, 169, 78, 0.25) 50%,
      rgba(30, 30, 30, 0.92) 100%
    ), url('${heroImage}')`
  };

  return (
    <div className="dashboard">
      {/* Hero Section - Clean, no action buttons */}
      <div className="dashboard-hero" style={heroStyle}>
        <div className="hero-content">
          <h1 className="hero-title">Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}!</h1>
          <p className="hero-subtitle">Managing Excellence in Student Transportation</p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        {/* Employees Card */}
        <div className="stat-card" onClick={() => navigate('/employees')} style={{ boxShadow: '0 0 25px rgba(212,169,78,0.35), 0 0 50px rgba(212,169,78,0.15), 0 0 80px rgba(212,169,78,0.05)' }}>
          <div className="stat-icon employees-icon">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
            </svg>
          </div>
          <div className="stat-content">
            <h3>Employees</h3>
            <div className="stat-numbers">
              <span className="stat-value">{stats?.employees?.total || 0}</span>
              <span className="stat-label">Total</span>
            </div>
            <div className="stat-detail">
              <span>{stats?.employees?.active || 0} Active</span>
              <span>{stats?.employees?.drivers || 0} Drivers</span>
            </div>
          </div>
        </div>

        {/* Fleet Card */}
        <div className="stat-card" onClick={() => navigate('/fleet')} style={{ boxShadow: '0 0 25px rgba(212,169,78,0.35), 0 0 50px rgba(212,169,78,0.15), 0 0 80px rgba(212,169,78,0.05)' }}>
          <div className="stat-icon fleet-icon"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M17 20H7V21C7 21.55 6.55 22 6 22H5C4.45 22 4 21.55 4 21V20H3V12L5 6H19L21 12V20H20V21C20 21.55 19.55 22 19 22H18C17.45 22 17 21.55 17 21V20ZM5 14H19V12H5V14ZM6.5 17C7.33 17 8 16.33 8 15.5S7.33 14 6.5 14 5 14.67 5 15.5 5.67 17 6.5 17ZM17.5 17C18.33 17 19 16.33 19 15.5S18.33 14 17.5 14 16 14.67 16 15.5 16.67 17 17.5 17ZM5 11H19L17.5 7H6.5L5 11Z"/></svg></div>
          <div className="stat-content">
            <h3>Fleet</h3>
            <div className="stat-numbers">
              <span className="stat-value">{stats?.fleet?.total || 0}</span>
              <span className="stat-label">Vehicles</span>
            </div>
            <div className="stat-detail">
              <span>{stats?.fleet?.active || 0} Active</span>
              <span>{stats?.fleet?.maintenance || 0} In Maintenance</span>
            </div>
          </div>
        </div>

        {/* Certifications Card */}
        <div className="stat-card" onClick={() => navigate('/certifications')} style={{ boxShadow: '0 0 25px rgba(212,169,78,0.35), 0 0 50px rgba(212,169,78,0.15), 0 0 80px rgba(212,169,78,0.05)' }}>
          <div className="stat-icon cert-icon">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>
            </svg>
          </div>
          <div className="stat-content">
            <h3>Certifications</h3>
            <div className="stat-numbers">
              <span className="stat-value warning">{stats?.alerts?.certifications_expiring || 0}</span>
              <span className="stat-label">Expiring Soon</span>
            </div>
          </div>
        </div>

        {/* Alerts Card */}
        <div className="stat-card alerts-card" style={{ boxShadow: '0 0 25px rgba(212,169,78,0.35), 0 0 50px rgba(212,169,78,0.15), 0 0 80px rgba(212,169,78,0.05)' }}>
          <div className="stat-icon alerts-icon">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"/>
            </svg>
          </div>
          <div className="stat-content">
            <h3>Alerts</h3>
            <div className="stat-detail">
              <span>{stats?.alerts?.unread_messages || 0} Unread Messages</span>
              <span>{stats?.alerts?.active_pages || 0} Active Pages</span>
            </div>
          </div>
        </div>
      </div>

      {/* Attendance Widgets — permission-derived (TO1a), split view/approve */}
      {canSeeAttendance && (
        <div className="attendance-widgets">
          {canSeeWhosOut && (
            <WhosOutWidget 
              employees={outToday} 
              loading={loadingAttendance}
              onViewAll={() => navigate('/attendance')}
              onViewCalendar={() => navigate('/whos-out')}
            />
          )}
          {canSeePendingRequests && (
            <PendingRequestsWidget 
              requests={pendingRequests}
              loading={loadingAttendance}
              onViewAll={() => navigate('/attendance')}
            />
          )}
        </div>
      )}

      <div className="dashboard-message">
        <p>Have a great day!</p>
      </div>
    </div>
  );
};

export default Dashboard;
